import { all, call, put, select, takeEvery } from "redux-saga/effects";

import { post, del } from "redux/fetch";
import { ANSWER_SOURCE, QUESTION_FLOW } from "lib/constants";
import rollbar, { rollbarXHRError } from "lib/rollbar";

import { candidateIncrAnswerCount } from "redux/candidate/actions";
import { checkinRemoveAnswer, checkinSetAnswer } from "redux/checkin/actions";
import { uiSetAsyncError, uiSetSubmitting } from "redux/ws/actions";

import {
    questionAnswer,
    questionCurrentSet,
    questionInsert,
    questionNext,
    questionSetAnswer,
    questionUndo,
} from "./actions";
import { filterQuestions } from "./selectors";

/**
 * Adds an answer/response to a question for a user
 */
function* answerQuestion({
    payload: { questionID, response, followUp, selfIdentified, source, flow, ...extraData },
}) {
    yield put(uiSetSubmitting(true));

    try {
        const data = {
            response,
            source,
            question_id: questionID,
            follow_up_response: followUp || null,
            ...extraData,
        };

        if (selfIdentified) {
            data.anonymous = false;
        }

        // Don't save preview checkin_hires
        let result;

        if (flow !== QUESTION_FLOW.CHECK_IN || !data.is_preview) {
            result = yield call(post, "/api/hire/v1/answer/", data);
        } else {
            result = {
                answer: {
                    id: -1,
                    response,
                    value: response.value,
                    has_follow_up: response.has_follow_up,
                    follow_up_optional: response.follow_up_optional,
                },
            };
        }

        yield put(
            questionSetAnswer({
                questionID,
                response: result.answer,
            }),
        );

        if (flow === QUESTION_FLOW.CHECK_IN) {
            yield put(
                checkinSetAnswer({
                    answer_id: result.answer.id,
                    ...data,
                }),
            );
        }

        yield put(candidateIncrAnswerCount(1));

        const { index, lastQuestion } = yield select((state) => {
            const { current, questions } = state.questions;
            return {
                index: current,
                lastQuestion: current === questions.length - 1,
            };
        });

        if (source === ANSWER_SOURCE.PROFILE && lastQuestion && result.next_questions) {
            yield put(
                questionInsert({
                    index,
                    questions: result.next_questions,
                }),
            );
        }

        // At some point we might need to decouple this logic from question answering
        // in the same way the application sagas kinda allow it
        yield put(questionNext());
    } catch (e) {
        rollbarXHRError("Failed to post question", e, {
            questionID,
            response,
            followUp,
            source,
        });
        yield put(uiSetAsyncError("Something went wrong. Please try again."));
    } finally {
        yield put(uiSetSubmitting(false));
    }
}

/**
 * Deletes an answer to a question for a user
 */
function* undoQuestion({ payload: questionID }) {
    const answerID = yield select(({ questions }) => {
        const question = questions.questions.filter((q) => q.id === questionID).pop();
        if (!question || !question.response) {
            return null;
        }

        return question.response.id;
    });
    const questionIndex = yield select(({ questions }) =>
        questions.questions.findIndex((q) => q.id === questionID),
    );

    if (!answerID) {
        rollbar.warn("Attempt to undo question with no answer id?", {
            questionID,
            answerID,
        });
        return;
    }

    yield put(uiSetSubmitting(true));

    try {
        yield call(del, `/api/hire/v1/answer/${answerID}/`);

        yield put(
            questionSetAnswer({
                questionID,
                response: null,
            }),
        );

        yield put(checkinRemoveAnswer(questionID));

        yield put(candidateIncrAnswerCount(-1));

        const { current, questions } = yield select((state) => ({
            current: state.questions.current,
            questions: state.questions.questions,
        }));

        if (questionIndex > -1) {
            yield put(questionCurrentSet(questionIndex));
        } else if (current === null) {
            yield put(questionCurrentSet(questions.length - 1));
        } else if (current > 0) {
            yield put(questionCurrentSet(current - 1));
        }
    } catch (e) {
        rollbarXHRError("Failed to delete answer", e);
        yield put(uiSetAsyncError("Something went wrong. Please try again."));
    } finally {
        yield put(uiSetSubmitting(false));
    }
}

/**
 * Cycles to the next question in the list
 */
function* nextQuestion() {
    const next = yield select((state) => {
        const { current, questions } = state.questions;
        if (current === null) {
            return 0;
        }

        const filtered = filterQuestions(state);

        const nextQ = filtered.shift();
        return nextQ ? questions.indexOf(nextQ) : null;
    });

    yield put(questionCurrentSet(next));
}

export default function* questionSagas() {
    yield all([
        takeEvery(questionAnswer, answerQuestion),
        takeEvery(questionNext, nextQuestion),
        takeEvery(questionUndo, undoQuestion),
    ]);
}
